import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import WelcomeHeader from "../components/WelcomeHeader";

const getAuthToken = () => {
  const rawToken =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    "";

  return rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
};

const agents = [
  {
    name: "Agente de Pré Venda",
    type: "Externo",
    operation: "Pré-Venda",
    status: "Ativo",
    channel: "WhatsApp",
    integrations: "CRM, WhatsApp",
    function:
      "Qualifica leads, conduz o atendimento inicial, identifica interesse e aciona o próximo passo comercial.",
  },
  {
    name: "Agente de Pós-Venda",
    type: "Externo",
    operation: "Pós-Venda",
    status: "Ativo",
    channel: "WhatsApp",
    integrations: "Sienge, CV CRM",
    function:
      "Atende clientes em solicitações de segunda via de boleto, extrato, relatório de imposto de renda, informações financeiras e assistência técnica, incluindo abertura e consulta de chamados.",
  },
  {
    name: "Agente do Corretor",
    type: "Interno",
    operation: "Corretores",
    status: "Disponível",
    channel: "Portal, WhatsApp ou App futuramente",
    integrations: "CRM",
    function:
      "Apoia corretores e gestores com alertas, carteira, próximos passos e resumo das oportunidades.",
  },
  {
    name: "Agente de Cobrança",
    type: "Externo",
    operation: "Cobrança",
    status: "Em implantação",
    channel: "WhatsApp",
    integrations: "Sienge, CRM, WhatsApp",
    function:
      "Apoia a régua de cobrança, o acompanhamento de inadimplência e o envio de lembretes aos clientes.",
  },
  {
    name: "Agente de Contabilidade",
    type: "Interno",
    operation: "Contabilidade",
    status: "Ativo",
    channel: "Portal",
    integrations: "Base documental, dados internos ou sistemas configurados",
    function:
      "Esclarece dúvidas, sintetiza informações, gera análises, orientações e planos de ação para apoiar rotinas contábeis.",
  },
  {
    name: "Agente de Conteúdo",
    type: "Interno",
    operation: "Conteúdo",
    status: "Ativo",
    channel: "WhatsApp ou Portal",
    integrations: "Base de conhecimento, documentos ou canais configurados",
    function:
      "Apoia a criação, revisão e organização de conteúdos institucionais, comerciais ou operacionais.",
  },
];

const normalizeAgentType = (type) => {
  const normalized = String(type || "").trim().toUpperCase();
  const labels = {
    INTERNO: "Interno",
    EXTERNO: "Externo",
    AMBOS: "Ambos",
  };

  return labels[normalized] || "Não informado";
};

const formatChannel = (channel) => {
  if (!channel) return "-";

  return String(channel)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      const normalized = item.toUpperCase();
      if (normalized === "WHATSAPP") return "WhatsApp";
      if (normalized === "PORTAL") return "Portal";
      return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
    })
    .join(", ");
};

const mapApiAgent = (agent, status) => ({
  id: `${status}-${agent?.id_agente ?? agent?.nome}`,
  name: agent?.nome || "Agente sem nome",
  type: normalizeAgentType(agent?.tipo),
  operation: agent?.produto?.nome_produto || "-",
  status,
  channel: formatChannel(agent?.canal_padrao),
  function: agent?.descricao || "",
});

const buildAgentGroupsFromResponse = (data) => ({
  contratados: Array.isArray(data?.contratados)
    ? data.contratados.map((agent) => mapApiAgent(agent, "Contratado"))
    : [],
  disponiveis: Array.isArray(data?.disponiveis)
    ? data.disponiveis.map((agent) => mapApiAgent(agent, "Disponível"))
    : [],
});

const defaultAgentGroups = {
  contratados: agents.filter((agent) => agent.status === "Ativo"),
  disponiveis: agents.filter((agent) => agent.status !== "Ativo"),
};

const typeBadgeSx = {
  Externo: {
    color: "#dbeafe",
    borderColor: "rgba(147, 197, 253, 0.28)",
    backgroundColor: "rgba(30, 41, 59, 0.62)",
  },
  Interno: {
    color: "#60a5fa",
    borderColor: "rgba(96, 165, 250, 0.42)",
    backgroundColor: "rgba(30, 64, 175, 0.22)",
  },
  Ambos: {
    color: "#c4b5fd",
    borderColor: "rgba(196, 181, 253, 0.42)",
    backgroundColor: "rgba(91, 33, 182, 0.2)",
  },
  "Não informado": {
    color: "#94a3b8",
    borderColor: "rgba(148, 163, 184, 0.32)",
    backgroundColor: "rgba(100, 116, 139, 0.18)",
  },
};

const statusBadgeSx = {
  Contratado: {
    color: "#4ade80",
    borderColor: "rgba(74, 222, 128, 0.35)",
    backgroundColor: "rgba(22, 101, 52, 0.24)",
  },
  Ativo: {
    color: "#4ade80",
    borderColor: "rgba(74, 222, 128, 0.35)",
    backgroundColor: "rgba(22, 101, 52, 0.24)",
  },
  Disponível: {
    color: "#38bdf8",
    borderColor: "rgba(56, 189, 248, 0.35)",
    backgroundColor: "rgba(14, 116, 144, 0.22)",
  },
  "Em implantação": {
    color: "#f59e0b",
    borderColor: "rgba(245, 158, 11, 0.38)",
    backgroundColor: "rgba(146, 64, 14, 0.26)",
  },
};

const Badge = ({ label, variant }) => {
  const styles = variant === "type" ? typeBadgeSx[label] : statusBadgeSx[label];

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.15,
        py: 0.38,
        borderRadius: 1.2,
        border: "1px solid",
        fontSize: 12.2,
        lineHeight: 1,
        fontWeight: 750,
        whiteSpace: "nowrap",
        ...(styles || {}),
      }}
    >
      {label}
    </Box>
  );
};

const InlineSectionHeader = ({ title, description }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={{ xs: 0.3, sm: 1.2 }}
    alignItems={{ xs: "flex-start", sm: "baseline" }}
  >
    <Typography
      variant="overline"
      sx={{ color: "text.secondary", fontSize: 14, fontWeight: 900, lineHeight: 1.4 }}
    >
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {description}
    </Typography>
  </Stack>
);

const AgentsTableSection = ({ agents, tokens }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "8px",
      color: "inherit",
      background: tokens.tableBg,
      border: `1px solid ${tokens.border}`,
      boxShadow: tokens.shadow,
      overflow: "hidden",
    }}
  >
    <TableContainer
      sx={{
        color: "inherit",
        overflowX: "auto",
      }}
    >
      <Table sx={{ minWidth: 860 }}>
        <TableHead>
          <TableRow
            sx={{
              background: tokens.tableHeadBg,
              "& th": {
                borderBottom: `1px solid ${tokens.border}`,
                color: tokens.subtleText,
                fontWeight: 850,
                fontSize: 12.4,
                py: 1.25,
              },
            }}
          >
            <TableCell>Agente</TableCell>
            <TableCell>Tipo</TableCell>
            <TableCell>Operação</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Canal</TableCell>
            <TableCell>Função</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agents.length > 0 ? (
            agents.map((agent) => (
              <TableRow
                key={agent.id || agent.name}
                sx={{
                  "&:hover td": {
                    backgroundColor: tokens.rowHover,
                  },
                  "& td": {
                    borderBottom: `1px solid ${tokens.rowBorder}`,
                    color: tokens.softText,
                    py: 1.35,
                    verticalAlign: "top",
                    fontSize: 13.1,
                    lineHeight: 1.42,
                  },
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ minWidth: 240 }}>
                  <Typography sx={{ color: tokens.pageText, fontWeight: 850, fontSize: 13.5 }}>
                    {agent.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Badge label={agent.type} variant="type" />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>{agent.operation}</TableCell>
                <TableCell>
                  <Badge label={agent.status} variant="status" />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>{agent.channel}</TableCell>
                <TableCell sx={{ minWidth: 280, maxWidth: 430, color: agent.function ? tokens.softText : tokens.subtleText }}>
                  {agent.function || "Sem descrição cadastrada."}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} sx={{ py: 3, color: tokens.mutedText, borderBottom: 0 }}>
                Nenhum agente nesta categoria.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const getThemeTokens = (isDarkMode) =>
  isDarkMode
    ? {
        pageText: "#f8fafc",
        mutedText: "#b7c0d3",
        softText: "#dbe4f0",
        subtleText: "#8994aa",
        cardBg: "rgba(8, 13, 28, 0.9)",
        cardSoftBg: "rgba(12, 19, 38, 0.74)",
        tableBg: "rgba(8, 13, 28, 0.9)",
        tableHeadBg: "rgba(12, 19, 38, 0.74)",
        border: "rgba(148, 163, 184, 0.16)",
        rowBorder: "rgba(148, 163, 184, 0.14)",
        rowHover: "rgba(148, 163, 184, 0.06)",
        primary: "#a78bfa",
        primarySoft: "rgba(124, 58, 237, 0.16)",
        secondary: "#93c5fd",
        shadow: "0 18px 48px rgba(0, 0, 0, 0.22)",
        background: "#050817",
      }
    : {
        pageText: "#0f172a",
        mutedText: "#475569",
        softText: "#1e293b",
        subtleText: "#64748b",
        cardBg: "rgba(255, 255, 255, 0.92)",
        cardSoftBg: "rgba(248, 250, 252, 0.9)",
        tableBg: "rgba(255, 255, 255, 0.92)",
        tableHeadBg: "rgba(241, 245, 249, 0.9)",
        border: "rgba(15, 23, 42, 0.1)",
        rowBorder: "rgba(148, 163, 184, 0.2)",
        rowHover: "rgba(241, 245, 249, 0.62)",
        primary: "#6d28d9",
        primarySoft: "rgba(109, 40, 217, 0.08)",
        secondary: "#6384d8",
        shadow: "0 18px 42px rgba(15, 23, 42, 0.06)",
        background: "#f6f8fb",
      };

const Agentes = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const tokens = getThemeTokens(isDarkMode);
  const [agentGroups, setAgentGroups] = useState(defaultAgentGroups);

  useEffect(() => {
    let isMounted = true;

    const fetchAgents = async () => {
      const token = getAuthToken();
      const baseURL = process.env.REACT_APP_API_BASE_URL;

      if (!token || !baseURL) return;

      try {
        const response = await axios.get(`${baseURL}/agentes`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!isMounted) return;

        setAgentGroups(buildAgentGroupsFromResponse(response?.data));
      } catch (error) {
        // Mantem os dados estaticos enquanto o endpoint estiver indisponivel.
      }
    };

    fetchAgents();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Stack spacing={3}>
      <WelcomeHeader title="Agentes" subtitle="" />

      <InlineSectionHeader
        title="Agentes contratados"
        description={`${agentGroups.contratados.length} agente${agentGroups.contratados.length === 1 ? "" : "s"} liberado${agentGroups.contratados.length === 1 ? "" : "s"} para esta empresa`}
      />
      <AgentsTableSection agents={agentGroups.contratados} tokens={tokens} />

      <InlineSectionHeader
        title="Agentes disponíveis"
        description={`${agentGroups.disponiveis.length} ${
          agentGroups.disponiveis.length === 1 ? "agente disponível no catálogo" : "agentes disponíveis no catálogo"
        }`}
      />
      <AgentsTableSection agents={agentGroups.disponiveis} tokens={tokens} />

      {false && (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          color: "inherit",
          background: tokens.tableBg,
          border: `1px solid ${tokens.border}`,
          boxShadow: isDarkMode
            ? "inset 0 1px 0 rgba(255,255,255,0.03)"
            : "0 16px 32px rgba(15, 23, 42, 0.06)",
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow
              sx={{
                background: tokens.tableHeadBg,
                "& th": {
                borderBottom: `1px solid ${tokens.border}`,
                color: tokens.subtleText,
                fontWeight: 850,
                fontSize: 12.4,
                py: 1.25,
              },
            }}
          >
            <TableCell>Agente</TableCell>
            <TableCell>Tipo</TableCell>
              <TableCell>Operação</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Canal</TableCell>
              <TableCell>Função</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...agentGroups.contratados, ...agentGroups.disponiveis].map((agent) => (
              <TableRow
                key={agent.id || agent.name}
                sx={{
                  "&:hover td": {
                    backgroundColor: tokens.rowHover,
                  },
                  "& td": {
                    borderBottom: `1px solid ${tokens.rowBorder}`,
                    color: tokens.softText,
                    py: 1.35,
                    verticalAlign: "top",
                    fontSize: 13.1,
                    lineHeight: 1.42,
                  },
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                <TableCell sx={{ minWidth: 240 }}>
                  <Typography sx={{ color: tokens.pageText, fontWeight: 850, fontSize: 13.5 }}>
                    {agent.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Badge label={agent.type} variant="type" />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>{agent.operation}</TableCell>
                <TableCell>
                  <Badge label={agent.status} variant="status" />
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>{agent.channel}</TableCell>
                <TableCell sx={{ minWidth: 280, maxWidth: 430, color: agent.function ? tokens.softText : tokens.subtleText }}>
                  {agent.function || "Sem descrição cadastrada."}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Stack>
  );
};

export default Agentes;
